import { useEffect, useState } from "react";
import { useParams, Link, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { BadgeCheck, ArrowLeft, Trophy, Crosshair, Target, Zap } from "lucide-react";
import { DiscordBadge } from "@/components/DiscordBadge";

export default function PlayerCareerCard() {
  const { handle, playerId } = useParams();
  const [searchParams] = useSearchParams();
  const teamIdParam = searchParams.get("team_id");
  const [data, setData] = useState(null);
  const [ambiguous, setAmbiguous] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [tape, setTape] = useState([]); // Iter77 · match tape appearances

  useEffect(() => {
    setData(null);
    setAmbiguous(null);
    setNotFound(false);
    setTape([]);
    const careerUrl = playerId
      ? `/players/by-id/${encodeURIComponent(playerId)}`
      : `/players/career/${encodeURIComponent(handle)}${teamIdParam ? `?team_id=${encodeURIComponent(teamIdParam)}` : ""}`;
    api.get(careerUrl)
      .then((r) => {
        if (r.data?.ambiguous) {
          setAmbiguous(r.data);
        } else {
          setData(r.data);
        }
      })
      .catch((e) => setNotFound(e?.response?.status === 404));
    const tapeHandle = playerId ? null : handle;
    if (tapeHandle) {
      api.get(`/players/${encodeURIComponent(tapeHandle)}/game-links?limit=10`)
        .then((r) => setTape(r.data || []))
        .catch(() => setTape([]));
    }
  }, [handle, playerId, teamIdParam]);

  if (ambiguous) {
    return (
      <div className="max-w-3xl mx-auto p-4 sm:p-8" data-testid="player-ambiguous">
        <Link to="/players" className="text-[10px] font-mono uppercase tracking-widest text-gray-500 hover:text-cyan-400 inline-flex items-center gap-1 mb-6">
          <ArrowLeft className="w-3 h-3" /> Players
        </Link>
        <div className="text-[10px] font-mono uppercase tracking-widest text-[#F4D03F] mb-2">/ MULTIPLE MATCHES</div>
        <h1 className="font-heading text-3xl sm:text-4xl font-black tracking-tight uppercase mb-2">
          {ambiguous.handle}
        </h1>
        <p className="text-gray-400 text-sm mb-6">
          This handle is on {ambiguous.candidates.length} rosters — pick the one you meant.
        </p>
        <ul className="space-y-2">
          {ambiguous.candidates.map((c) => (
            <li key={c.player_id}>
              <Link
                to={`/player-id/${c.player_id}`}
                data-testid={`ambiguous-pick-${c.player_id}`}
                className="flex items-center justify-between gap-3 border border-[#232330] hover:border-cyan-400 bg-[#101015] px-4 py-3 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <span
                    className="font-mono text-[10px] uppercase tracking-widest border px-2 py-0.5 font-bold"
                    style={{ borderColor: c.team?.logo_color || "#8b93f9", color: c.team?.logo_color || "#8b93f9" }}
                  >
                    {c.team?.tag || "FREE AGENT"}
                  </span>
                  <span className="font-heading font-bold uppercase tracking-tight">{c.handle}</span>
                  {c.hll_verified && <BadgeCheck className="w-3.5 h-3.5 text-cyan-400" />}
                </div>
                <div className="font-mono text-[10px] uppercase tracking-widest text-gray-500">
                  {c.matches || 0} match{(c.matches || 0) === 1 ? "" : "es"}
                </div>
              </Link>
            </li>
          ))}
        </ul>
      </div>
    );
  }

  if (notFound) {
    return (
      <div className="max-w-3xl mx-auto p-8 text-center" data-testid="player-not-found">
        <div className="text-[10px] font-mono uppercase tracking-widest text-red-500 mb-2">/ ROSTER MISS</div>
        <div className="font-heading font-black text-4xl mb-3">The scouts couldn't find {handle || playerId}</div>
        <p className="text-gray-400 mb-6">This handle isn't on any active roster.</p>
        <Link to="/players" className="text-cyan-400 border border-cyan-400 hover:bg-cyan-400 hover:text-black px-4 py-2 uppercase text-xs font-mono tracking-widest inline-flex items-center gap-2">
          <ArrowLeft className="w-3 h-3" /> Back to players
        </Link>
      </div>
    );
  }

  if (!data) {
    return <div className="max-w-4xl mx-auto p-8 text-gray-500 font-mono text-xs uppercase tracking-widest" data-testid="player-loading">Loading dossier…</div>;
  }

  const teamColor = data.team?.logo_color || "#F4D03F";

  return (
    <div className="max-w-5xl mx-auto p-4 sm:p-8 space-y-6" data-testid={`player-card-${data.handle}`}>
      <Link to="/players" className="text-[10px] font-mono uppercase tracking-widest text-gray-500 hover:text-cyan-400 inline-flex items-center gap-1">
        <ArrowLeft className="w-3 h-3" /> Players
      </Link>

      {/* Hero */}
      <div
        className="border p-6 sm:p-8 relative overflow-hidden"
        style={{ borderColor: teamColor, background: `linear-gradient(135deg, ${teamColor}12 0%, transparent 60%)` }}
        data-testid="player-hero"
      >
        <div className="text-[10px] font-mono uppercase tracking-[0.3em] mb-2" style={{ color: teamColor }}>
          / PLAYER DOSSIER
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <div className="font-heading font-black text-4xl sm:text-6xl tracking-tight uppercase">
            {data.handle}
          </div>
          {data.hll_verified && (
            <span className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest text-cyan-400 border border-cyan-400 px-2 py-1" data-testid="player-hll-verified">
              <BadgeCheck className="w-3 h-3" /> HLL Verified
            </span>
          )}
          {data.team_discord_linked && <DiscordBadge handle={data.handle} size={14} testid="player-card-discord" />}
        </div>
        <div className="text-sm font-mono uppercase tracking-widest text-gray-400 mt-2">
          {data.team ? (
            <>
              <span style={{ color: teamColor }} className="font-bold">{data.team.tag}</span>
              {" · "}{data.team.name}
              {data.role && <> · {data.role}</>}
            </>
          ) : "Free Agent"}
        </div>
      </div>

      {/* Stats grid */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="player-stats-grid">
        {[
          { label: "Kills", val: data.stats.kills, icon: Crosshair, color: "text-[#FF007F]" },
          { label: "Deaths", val: data.stats.deaths, icon: Target, color: "text-gray-400" },
          { label: "K/D", val: data.stats.kd, icon: Zap, color: "text-cyan-400" },
          { label: "Matches", val: data.stats.matches, icon: Trophy, color: "text-[#F4D03F]" },
          { label: "Rating", val: data.stats.rating, icon: Zap, color: "text-[#B084FF]" },
        ].map((s) => (
          <div key={s.label} className="border border-[#232330] bg-[#101015] p-5">
            <s.icon className={`w-4 h-4 mb-2 ${s.color}`} />
            <div className="font-heading font-black text-3xl">{s.val ?? 0}</div>
            <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-gray-500 mt-1">{s.label}</div>
          </div>
        ))}
      </div>

      {/* Trophies */}
      {data.trophies?.length > 0 && (
        <div className="border border-[#F4D03F]/40 bg-[#F4D03F]/5 p-5" data-testid="player-trophies">
          <div className="text-[10px] font-mono uppercase tracking-widest text-[#F4D03F] mb-3">/ TROPHY CASE</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
            {data.trophies.map((t, i) => (
              <div key={i} className="border border-[#F4D03F]/30 p-4 flex items-center gap-3">
                <Trophy className="w-8 h-8 text-[#F4D03F]" />
                <div>
                  <div className="font-heading font-black uppercase tracking-tight">{t.trophy || "Champion"}</div>
                  <div className="text-[10px] font-mono uppercase tracking-widest text-gray-500">Season {t.season || "—"}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Career matches */}
      <div className="border border-[#232330] bg-[#101015]" data-testid="player-career-matches">
        <div className="p-4 border-b border-[#232330]">
          <div className="text-[10px] font-mono uppercase tracking-widest text-gray-500">/ MATCH HISTORY</div>
          <div className="font-heading font-black text-xl uppercase tracking-tight mt-1">Recent 50 matches</div>
        </div>
        {data.career_matches?.length ? (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[10px] font-mono uppercase tracking-widest text-gray-500 border-b border-[#232330]">
                <th className="px-4 py-2">Date</th>
                <th className="px-4 py-2">Map</th>
                <th className="px-4 py-2">Score</th>
                <th className="px-4 py-2 text-right">Result</th>
              </tr>
            </thead>
            <tbody>
              {data.career_matches.map((m) => (
                <tr key={m.fixture_id} className="border-b border-[#232330]">
                  <td className="px-4 py-2 text-xs text-gray-400 font-mono">
                    {m.date ? new Date(m.date).toLocaleDateString() : "—"}
                  </td>
                  <td className="px-4 py-2 text-xs uppercase tracking-widest">{m.map || "—"}</td>
                  <td className="px-4 py-2 font-mono">
                    <span className={m.won ? "text-cyan-400" : "text-gray-500"}>{m.own_score ?? "—"}</span>
                    <span className="text-gray-600 mx-1">·</span>
                    <span className="text-gray-500">{m.opp_score ?? "—"}</span>
                  </td>
                  <td className="px-4 py-2 text-right">
                    <span className={`text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 ${m.won ? "text-cyan-400 border border-cyan-400" : "text-gray-500 border border-[#232330]"}`}>
                      {m.won ? "WIN" : "LOSS"}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div className="p-8 text-center text-gray-500 font-mono text-xs uppercase tracking-widest">
            No completed matches yet. Watch this space.
          </div>
        )}
      </div>

      {tape.length > 0 && (
        <div className="mt-10 border border-[#232330] bg-[#101015]" data-testid="player-match-tape">
          <div className="border-b border-[#232330] p-4 flex items-center justify-between">
            <div>
              <div className="text-[10px] font-mono uppercase tracking-widest text-pink-400">/ MATCH TAPE</div>
              <div className="font-heading font-black text-xl">Last {tape.length} appearance{tape.length === 1 ? "" : "s"}</div>
            </div>
            <div className="text-[10px] font-mono text-gray-500 uppercase tracking-widest">VODs · Clips · Highlights</div>
          </div>
          <div className="divide-y divide-[#232330]">
            {tape.map((t) => (
              <a
                key={t.id}
                href={t.url}
                target="_blank"
                rel="noreferrer"
                data-testid={`player-tape-${t.id}`}
                className="flex items-center justify-between gap-3 px-4 py-3 hover:bg-[#05050A] group"
              >
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 border border-[#232330] text-cyan-400">
                      {t.kind}
                    </span>
                    <span className="text-[10px] font-mono uppercase tracking-widest text-gray-500">{t.source}</span>
                    {t.round && (
                      <span className="text-[10px] font-mono uppercase tracking-widest text-gray-500">· R{t.round}</span>
                    )}
                  </div>
                  <div className="text-sm text-white truncate mt-1 group-hover:text-pink-400">
                    {t.title || t.url}
                  </div>
                </div>
                <div className="text-[10px] font-mono uppercase tracking-widest text-gray-600 shrink-0">
                  {new Date(t.uploaded_at).toLocaleDateString()}
                </div>
              </a>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
